'use client';

import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {useI18n} from '@udemy-v2/i18n';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {Icon} from '@udemy-v2/icons';
import {Button} from '@udemy-v2/react-core-components';
import {FooterButtons, Modal} from '@udemy-v2/react-dialog-components';
import {ContentArea} from '@udemy-v2/react-layout-components';
import {
    Carousel,
    type CarouselPagerButtonProps,
    CarouselNextButton,
    CarouselPreviousButton,
    CarouselSlide,
    useCarouselSlideLayout4Cards,
} from '@udemy-v2/react-carousel-components';
import {Loader} from '@udemy-v2/react-reveal-components';

import {
    type CourseReview,
    fetchCourseReviews,
    fetchInstructorCourses,
} from '@/features/instructor-profile/lib/profile-api';
import type {CourseOption} from '@/features/instructor-profile/lib/types';

import styles from './reviews.module.scss';

const MAX_REVIEWS = 12;
/**
 * Reviews requested per course. The API returns them ordered by
 * `course_review_score__rank`, whose top slots are deliberately sampled across
 * the course's rating distribution — so a small fetch reliably surfaces
 * low-rated reviews. Over-fetching gives us a candidate pool to re-rank by
 * rating before capping at MAX_REVIEWS.
 */
const REVIEW_CANDIDATES_PER_COURSE = 15;
/** Gap between mobile cards in px — must match .mobile-track gap in SCSS. */
const MOBILE_CARD_GAP_PX = 12;

/** A review annotated with its position in its own course's ranked batch. */
interface RankedReview extends CourseReview {
    rank: number;
}

export interface ReviewsProps {
    featuredCourses?: CourseOption[];
    instructorId?: number;
}

export function Reviews({featuredCourses, instructorId}: ReviewsProps) {
    const {gettext} = useI18n();
    const isMobile = useMatchMedia('md-max') === true;

    const [reviews, setReviews] = useState<CourseReview[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let active = true;

        async function load() {
            try {
                // 1. Resolve which courses to pull reviews from.
                let courses: Pick<CourseOption, 'id' | 'title'>[] =
                    featuredCourses && featuredCourses.length > 0
                        ? featuredCourses
                        : [];

                if (courses.length === 0 && instructorId != null) {
                    const {results} = await fetchInstructorCourses(instructorId, 1);
                    courses = results.slice(0, 2);
                }

                if (courses.length === 0) {
                    if (active) setIsLoading(false);
                    return;
                }

                // 2. Fetch a candidate pool per course in parallel. allSettled so
                // that one failing course still renders the others.
                const settled = await Promise.allSettled(
                    courses.map((c) =>
                        fetchCourseReviews(c.id, REVIEW_CANDIDATES_PER_COURSE),
                    ),
                );

                // 3. Merge, annotating each review with its course title and its
                // position within that course's batch. Batch order is the API's
                // course_review_score__rank ordering, so the index is the rank.
                const merged: RankedReview[] = [];
                let anyFailed = false;
                settled.forEach((outcome, i) => {
                    if (outcome.status === 'rejected') {
                        anyFailed = true;
                        return;
                    }
                    outcome.value.results.forEach((r, rank) => {
                        merged.push({...r, courseTitle: courses[i].title, rank});
                    });
                });

                // 4. Highest rated first; ties fall back to the backend's quality
                // rank so course order never decides which of two 5.0s wins.
                merged.sort((a, b) => b.rating - a.rating || a.rank - b.rank);

                if (active) {
                    setReviews(merged.slice(0, MAX_REVIEWS));
                    if (anyFailed) setHasError(true);
                }
            } catch {
                if (active) setHasError(true);
            } finally {
                if (active) setIsLoading(false);
            }
        }

        load();
        return () => {
            active = false;
        };
        // Run once on mount — featured courses and instructorId are stable props.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isLoading && reviews.length === 0 && !hasError) return null;

    return (
        <div id="ratings" className={styles['reviews']}>
            <ContentArea>
                {isLoading ? (
                    <>
                        <div className={styles['header-row']}>
                            <h2 className={styles['heading']}>{gettext('What people say')}</h2>
                        </div>
                        <div className={styles['loader']}>
                            <Loader size="large" label={gettext('Loading reviews')} />
                        </div>
                    </>
                ) : isMobile ? (
                    <MobileReviewsList reviews={reviews} hasError={hasError} />
                ) : (
                    <ReviewsCarousel reviews={reviews} hasError={hasError} />
                )}
            </ContentArea>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Desktop carousel — same portalled-pager pattern as AllCourses/CoursesCarousel
// ---------------------------------------------------------------------------

function ReviewsCarousel({reviews, hasError}: {reviews: CourseReview[]; hasError: boolean}) {
    const {gettext} = useI18n();
    const baseSlideLayout = useCarouselSlideLayout4Cards();
    const slideLayout = {
        ...baseSlideLayout,
        containerClassName: styles['reviews-slide-layout'],
    };
    const [prevSlot, setPrevSlot] = useState<HTMLElement | null>(null);
    const [nextSlot, setNextSlot] = useState<HTMLElement | null>(null);

    const slides = reviews.map((review) => (
        <CarouselSlide
            key={review.id}
            aria-label={review.user.public_display_name}
            className={styles['carousel-slide']}
        >
            <ReviewCard review={review} />
        </CarouselSlide>
    ));

    const renderPreviousButton = (props: CarouselPagerButtonProps) =>
        prevSlot
            ? createPortal(
                  <CarouselPreviousButton
                      {...props}
                      size="small"
                      udStyle="secondary"
                      hideWhenDisabled={false}
                  />,
                  prevSlot,
              )
            : null;

    const renderNextButton = (props: CarouselPagerButtonProps) =>
        nextSlot
            ? createPortal(
                  <CarouselNextButton
                      {...props}
                      size="small"
                      udStyle="secondary"
                      hideWhenDisabled={false}
                  />,
                  nextSlot,
              )
            : null;

    return (
        <>
            <div className={styles['header-row']}>
                <h2 className={styles['heading']}>{gettext('What people say')}</h2>
                <div className={styles['pager']}>
                    <span ref={setPrevSlot} className={styles['pager-slot']} />
                    <span ref={setNextSlot} className={styles['pager-slot']} />
                </div>
            </div>
            <div className={styles['reviews-carousel']}>
                <Carousel
                    ariaLabel={gettext('What people say')}
                    slideLayout={slideLayout}
                    slides={slides}
                    renderPreviousButton={renderPreviousButton}
                    renderNextButton={renderNextButton}
                />
            </div>
            {hasError && (
                <p role="alert" className={styles['error']}>
                    {gettext('Some reviews could not be loaded. Please try again later.')}
                </p>
            )}
        </>
    );
}

// ---------------------------------------------------------------------------
// Mobile list — CSS scroll-snap + dot indicators
// ---------------------------------------------------------------------------

function MobileReviewsList({
    reviews,
    hasError,
}: {
    reviews: CourseReview[];
    hasError: boolean;
}) {
    const {gettext} = useI18n();
    const [activeDot, setActiveDot] = useState(0);
    const trackRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(() => {
        const track = trackRef.current;
        if (!track || !track.firstElementChild) return;
        const cardWidth = (track.firstElementChild as HTMLElement).offsetWidth;
        const index = Math.round(track.scrollLeft / (cardWidth + MOBILE_CARD_GAP_PX));
        setActiveDot(Math.min(index, reviews.length - 1));
    }, [reviews.length]);

    return (
        <>
            <h2 className={styles['heading']}>{gettext('What people say')}</h2>
            <div ref={trackRef} className={styles['mobile-track']} onScroll={handleScroll}>
                {reviews.map((review) => (
                    <div key={review.id} className={styles['mobile-card-wrapper']}>
                        <ReviewCard review={review} />
                    </div>
                ))}
            </div>
            {reviews.length > 1 && (
                <div className={styles['dots']} aria-hidden="true">
                    {reviews.map((_, i) => (
                        <span
                            key={i}
                            className={`${styles['dot']} ${i === activeDot ? styles['dot--active'] : ''}`}
                        />
                    ))}
                </div>
            )}
            {hasError && (
                <p role="alert" className={styles['error']}>
                    {gettext('Some reviews could not be loaded. Please try again later.')}
                </p>
            )}
        </>
    );
}

// ---------------------------------------------------------------------------
// ReviewCard + detail modal
// ---------------------------------------------------------------------------

/** Truncate review body at exactly this many lines. */
const REVIEW_LINE_CLAMP = 8;
/** JSDOM fallback: show the "Show" link when text exceeds this char count. */
const REVIEW_SHOW_MORE_CHAR_THRESHOLD = 200;

interface ReviewBodyState {
    /** Visible text — may end with "…" when truncated. */
    visibleText: string;
    isTruncated: boolean;
}

/**
 * Binary-searches the longest prefix of `fullText` that, together with
 * `"… Show"`, still fits within `REVIEW_LINE_CLAMP` lines of the body element.
 */
function truncateToLines(
    wrapper: HTMLElement,
    bodyClassName: string,
    fullText: string,
    showLabel: string,
): ReviewBodyState {
    if (!fullText || wrapper.clientWidth <= 0) {
        return {
            visibleText: fullText,
            isTruncated: fullText.length > REVIEW_SHOW_MORE_CHAR_THRESHOLD,
        };
    }

    const probe = document.createElement('p');
    probe.className = bodyClassName;
    probe.setAttribute('aria-hidden', 'true');
    Object.assign(probe.style, {
        position: 'absolute',
        visibility: 'hidden',
        pointerEvents: 'none',
        insetInlineStart: '-9999px',
        inlineSize: `${wrapper.clientWidth}px`,
        display: 'block',
        overflow: 'visible',
        maxBlockSize: 'none',
    });
    wrapper.appendChild(probe);

    try {
        // Measure one line height, excluding the top-padding that the body
        // class adds (padding-block-start: 24px). Without this correction,
        // maxH would include 9× the padding instead of just one.
        probe.textContent = '\u00A0';
        const paddingTopPx = parseFloat(getComputedStyle(probe).paddingBlockStart) || 0;
        const oneLineH = probe.scrollHeight - paddingTopPx;
        const maxH = paddingTopPx + REVIEW_LINE_CLAMP * oneLineH;

        // Check whether the full text already fits.
        probe.textContent = fullText;
        if (probe.scrollHeight <= maxH + 1) {
            return {visibleText: fullText, isTruncated: false};
        }

        // Binary-search for the cut point where "truncated… Show" still fits.
        let low = 0;
        let high = fullText.length;
        let bestIdx = 0;

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            probe.textContent = `${fullText.slice(0, mid).trimEnd()}… ${showLabel}`;
            if (probe.scrollHeight <= maxH + 1) {
                bestIdx = mid;
                low = mid + 1;
            } else {
                high = mid - 1;
            }
        }

        const visibleText = `${fullText.slice(0, bestIdx).trimEnd()}…`;
        return {visibleText, isTruncated: true};
    } finally {
        probe.remove();
    }
}

function ReviewCard({review}: {review: CourseReview}) {
    const {gettext, interpolate} = useI18n();
    const showLabel = gettext('Show');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [bodyState, setBodyState] = useState<ReviewBodyState>({
        visibleText: review.content,
        isTruncated: review.content.length > REVIEW_SHOW_MORE_CHAR_THRESHOLD,
    });
    const wrapperRef = useRef<HTMLDivElement>(null);

    const syncBodyState = useCallback(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        setBodyState(truncateToLines(wrapper, styles['body'], review.content, showLabel));
    }, [review.content, showLabel]);

    useLayoutEffect(() => {
        syncBodyState();

        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const observer = new ResizeObserver(syncBodyState);
        observer.observe(wrapper);
        window.addEventListener('resize', syncBodyState);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', syncBodyState);
        };
    }, [review.content, syncBodyState]);

    return (
        <>
            <article className={styles['review-card']}>
                <div className={styles['card-content']}>
                    <div className={styles['card-top']}>
                        <ReviewQuoteMeta review={review} />
                        <div ref={wrapperRef} className={styles['body-wrapper']}>
                            <p
                                data-testid="review-body"
                                className={styles['body']}
                            >
                                {bodyState.visibleText}
                                {bodyState.isTruncated && (
                                    <>
                                        {' '}
                                        <button
                                            type="button"
                                            className={styles['show-link']}
                                            onClick={() => setIsModalOpen(true)}
                                            data-preview-disabled
                                        >
                                            {showLabel}
                                        </button>
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className={styles['card-footer']}>
                        <p className={styles['reviewer']}>{review.user.public_display_name}</p>
                        {review.courseTitle && (
                            <p className={styles['course-name']}>
                                {interpolate(
                                    gettext('Course: %(title)s'),
                                    {title: review.courseTitle},
                                    true,
                                )}
                            </p>
                        )}
                    </div>
                </div>
            </article>
            <ReviewDetailModal
                review={review}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </>
    );
}

function ReviewQuoteMeta({review}: {review: CourseReview}) {
    return (
        <>
            <Icon
                glyph="quote"
                label={false}
                color="inherit"
                className={styles['quote-icon']}
            />
            <div className={styles['meta-row']}>
                <div className={styles['rating-tag']}>
                    <Icon glyph="rating-star" label={false} className={styles['star-icon']} />
                    <span className={styles['rating']}>{review.rating.toFixed(1)}</span>
                </div>
                <span className={styles['time-ago']}>
                    {review.created_formatted_with_time_since}
                </span>
            </div>
        </>
    );
}

interface ReviewDetailModalProps {
    review: CourseReview;
    isOpen: boolean;
    onClose: () => void;
}

function ReviewDetailModal({review, isOpen, onClose}: ReviewDetailModalProps) {
    const {gettext} = useI18n();

    return (
        <Modal title={review.courseTitle ?? ''} isOpen={isOpen} onClose={onClose}>
            <div className={styles['modal-content']}>
                <div className={styles['modal-main']}>
                    <ReviewQuoteMeta review={review} />
                    <p className={styles['modal-body']}>{review.content}</p>
                </div>
                <p className={styles['modal-reviewer']}>{review.user.public_display_name}</p>
            </div>
            <FooterButtons>
                <Button udStyle="primary" size="large" onClick={onClose}>
                    {gettext('Close')}
                </Button>
            </FooterButtons>
        </Modal>
    );
}
